import React, { useState } from 'react';
import { ButtonGroup, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Box, Typography, Grid, MenuItem } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import api from '../utils/apiClient';
import AdminControlDevice from './AdminControlDevice';
import { useAuth } from '../hooks/useAuth';
import { getErrorMessage } from '../utils/errorMessage';
import { isHosterOrAbove, isClient } from '../utils/roles';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Commands = ({ selectedRows, deviceIds, uptimeData, devices, onDataRefresh, onOpenMoveDialog, onTimelineOpenChange, onClearSelection }) => {
  const theme = useTheme();
  const { t } = useTranslation('admin');
  const { user } = useAuth();
  const [openDialog, setOpenDialog] = useState(false);
  const [commandType, setCommandType] = useState('');
  const [pools, setPools] = useState([
    { url: '', worker: '', password: '', priority: 0 },
    { url: '', worker: '', password: '', priority: 1 },
    { url: '', worker: '', password: '', priority: 2 }
  ]);
  const [timeRangeStart, setTimeRangeStart] = useState(dayjs().subtract(1, 'day'));
  const [timeRangeEnd, setTimeRangeEnd] = useState(dayjs());
  const [isLoading, setIsLoading] = useState(false);
  const [showAdminControl, setShowAdminControl] = useState(false);
  const [isTicketMode, setIsTicketMode] = useState(false);
  const [ticketTitle, setTicketTitle] = useState('');
  const [ticketDescription, setTicketDescription] = useState('');
  const [ticketPriority, setTicketPriority] = useState('normal');

  if (selectedRows.length === 0) return null;

  // Проверяем роль пользователя для доступа к админ функциям
  const isAdminOrManager = isHosterOrAbove(user?.role);
  const isClientTicketMode = isClient(user?.role);

  // Получаем ID устройств из выбранных строк
  const getDeviceIds = () => {
    if (deviceIds && deviceIds.length > 0) {
      return deviceIds;
    }
    // Если deviceIds не переданы, используем selectedRows как индексы
    return selectedRows.map(index => `device_${index}`);
  };

  const getCommandLabel = (cmd) => {
    switch (cmd) {
      case 'restart': return t('commandsLabelRestart');
      case 'hardreset': return t('commandsLabelHardreset');
      case 'change_pool': return t('commandsLabelChangePool');
      case 'log': return t('commandsLabelLog');
      case 'manual': return t('commandsLabelManual');
      default: return cmd;
    }
  };

  const openTicketDialog = (type) => {
    setCommandType(type);
    setIsTicketMode(true);
    setTicketTitle(t('commandsTicketTitleTemplate', { label: getCommandLabel(type), count: getDeviceIds().length }));
    setTicketDescription('');
    setTicketPriority('normal');
    setOpenDialog(true);
  };

  const createTicket = async (ticketType, commandPayload) => {
    if (getDeviceIds().length === 0) {
      alert(t('commandsAlertSelectDevicesTicket'));
      return;
    }
    if (!ticketTitle.trim() || ticketTitle.trim().length < 3) {
      alert(t('commandsAlertTitleTooShort'));
      return;
    }
    if (!ticketDescription.trim() || ticketDescription.trim().length < 5) {
      alert(t('commandsAlertDescriptionTooShort'));
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        device_ids: getDeviceIds(),
        ticket_type: ticketType,
        title: ticketTitle.trim(),
        description: ticketDescription.trim(),
        priority: ticketPriority,
        command_payload: commandPayload || undefined
      };

      const resp = await api.post(`${API_BASE_URL}tickets`, payload);
      const ticketNo = resp?.data?.ticket?.ticket_no;
      alert(ticketNo ? t('commandsTicketCreatedWithNo', { no: ticketNo }) : t('commandsTicketCreated'));

      if (onClearSelection) onClearSelection();
      setOpenDialog(false);
      resetForm();
    } catch (error) {
      alert(t('commandsErrorCreateTicket', { error: getErrorMessage(error) }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCommand = async (command) => {
    // Для роли user все действия создают тикет
    if (isClientTicketMode) {
      openTicketDialog(command);
      return;
    }

    if (command === 'change_pool') {
      setCommandType('change_pool');
      setIsTicketMode(false);
      setOpenDialog(true);
      return;
    }

    if (command === 'log') {
      setCommandType('log');
      setIsTicketMode(false);
      setOpenDialog(true);
      return;
    }

    // Для простых команд (restart, hardreset)
    await sendCommand(command);
  };

  const sendCommand = async (command, additionalData = {}) => {
    if (getDeviceIds().length === 0) {
      alert(t('commandsAlertSelectDevicesCommand'));
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        device_ids: getDeviceIds(),
        command: command,
        ...additionalData
      };

      await api.post(`${API_BASE_URL}agent/commands`, payload);

      alert(t('commandsCommandSuccess', { command }));

      // Закрываем диалог если он был открыт
      if (openDialog) {
        setOpenDialog(false);
        resetForm();
      }

      // Снимаем выделение после успешного выполнения команды
      if (onClearSelection) {
        onClearSelection();
      }
    } catch (error) {
      alert(t('commandsErrorCommand', { error: getErrorMessage(error) }));
      // При ошибке выделение остается
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePool = async () => {
    // Проверяем, что заполнены URL и Worker для всех пулов
    const validPools = pools.filter(pool => pool.url.trim() && pool.worker.trim());
    
    if (validPools.length === 0) {
      alert(t('commandsAlertFillPool'));
      return;
    }

    const poolData = {
      pools: validPools.map(pool => {
        const result = {
          url: pool.url.trim(),
          worker: pool.worker.trim(),
          priority: parseInt(pool.priority)
        };
        if (pool.password && pool.password.trim()) {
          result.password = pool.password.trim();
        }
        return result;
      })
    };

    if (isTicketMode) {
      await createTicket('change_pool', poolData);
      return;
    }
    await sendCommand('change_pool', poolData);
  };

  const handleRequestLogs = async () => {
    if (!timeRangeStart || !timeRangeEnd) {
      alert(t('commandsAlertSelectTimeRange'));
      return;
    }

    const logData = {
      time_range: {
        start: timeRangeStart.toISOString(),
        end: timeRangeEnd.toISOString()
      }
    };

    if (isTicketMode) {
      await createTicket('log', logData);
      return;
    }
    await sendCommand('log', logData);
  };

  const resetForm = () => {
    setPools([
      { url: '', worker: '', password: '', priority: 0 },
      { url: '', worker: '', password: '', priority: 1 },
      { url: '', worker: '', password: '', priority: 2 }
    ]);
    setTimeRangeStart(dayjs().subtract(1, 'day'));
    setTimeRangeEnd(dayjs());
    setCommandType('');
    setIsTicketMode(false);
    setTicketTitle('');
    setTicketDescription('');
    setTicketPriority('normal');
  };

  const handlePoolChange = (index, field, value) => {
    const newPools = [...pools];
    newPools[index] = { ...newPools[index], [field]: value };
    setPools(newPools);
  };

  const renderTicketFields = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
      <Typography variant="body2" color="text.secondary">
        {t('commandsSelectedDevices', { count: getDeviceIds().length })}
      </Typography>
      <TextField
        label={t('commandsFieldTitle')}
        value={ticketTitle}
        onChange={(e) => setTicketTitle(e.target.value)}
        fullWidth
        size="small"
      />
      <TextField
        select
        label={t('commandsFieldPriority')}
        value={ticketPriority}
        onChange={(e) => setTicketPriority(e.target.value)}
        fullWidth
        size="small"
      >
        <MenuItem value="low">{t('ticketsPriorityLow')}</MenuItem>
        <MenuItem value="normal">{t('ticketsPriorityNormal')}</MenuItem>
        <MenuItem value="high">{t('ticketsPriorityHigh')}</MenuItem>
        <MenuItem value="urgent">{t('ticketsPriorityUrgent')}</MenuItem>
      </TextField>
      <TextField
        label={t('commandsFieldDescription')}
        value={ticketDescription}
        onChange={(e) => setTicketDescription(e.target.value)}
        fullWidth
        multiline
        minRows={4}
        placeholder={t('commandsDescriptionPlaceholder')}
      />
    </Box>
  );

  const renderDialogContent = () => {
    switch (commandType) {
      case 'change_pool':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h6">{t('commandsChangePoolTitle')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('commandsChangePoolHint')}
            </Typography>

            {pools.map((pool, index) => (
              <Box key={index} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                  {t('commandsPoolIndex', { index: index + 1, priority: pool.priority })}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t('commandsPoolUrlLabel')}
                      value={pool.url}
                      onChange={(e) => handlePoolChange(index, 'url', e.target.value)}
                      placeholder="stratum+tcp://pool.example.com:3333"
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Worker"
                      value={pool.worker}
                      onChange={(e) => handlePoolChange(index, 'worker', e.target.value)}
                      placeholder="username.worker"
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t('commandsPoolPasswordLabel')}
                      value={pool.password || ''}
                      onChange={(e) => handlePoolChange(index, 'password', e.target.value)}
                      placeholder={t('commandsPoolPasswordPlaceholder')}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Box>
            ))}
            {isTicketMode && renderTicketFields()}
          </Box>
        );

      case 'log':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h6">{t('commandsLogTitle')}</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <DateTimePicker
                    label={t('commandsLogStart')}
                    value={timeRangeStart}
                    onChange={(newValue) => setTimeRangeStart(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small"
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DateTimePicker
                    label={t('commandsLogEnd')}
                    value={timeRangeEnd}
                    onChange={(newValue) => setTimeRangeEnd(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small"
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </LocalizationProvider>
            {isTicketMode && renderTicketFields()}
          </Box>
        );

      case 'restart':
      case 'hardreset':
      case 'manual':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h6">{getCommandLabel(commandType)}</Typography>
            <Typography variant="body2" color="text.secondary">
              {commandType === 'manual'
                ? t('commandsManualHint')
                : t('commandsTicketActionHint', { label: getCommandLabel(commandType) })}
            </Typography>
            {renderTicketFields()}
          </Box>
        );

      default:
        return null;
    }
  };

  const handleDialogAction = async () => {
    if (commandType === 'change_pool') {
      await handleChangePool();
    } else if (commandType === 'log') {
      await handleRequestLogs();
    } else if (isTicketMode && (commandType === 'restart' || commandType === 'hardreset' || commandType === 'manual')) {
      await createTicket(commandType);
    }
  };

  // Если показываем меню управления (только для admin и manager)
  if (showAdminControl && isAdminOrManager) {
    return (
      <AdminControlDevice
        selectedRows={selectedRows}
        deviceIds={deviceIds}
        uptimeData={uptimeData}
        devices={devices}
        onBack={() => setShowAdminControl(false)}
        onDataRefresh={onDataRefresh}
        onOpenMoveDialog={onOpenMoveDialog}
        onTimelineOpenChange={onTimelineOpenChange}
        onClearSelection={onClearSelection}
      />
    );
  }

  // Обычное меню команд
  return (
    <>
      <div
        style={{
          position: 'fixed',
          top: 'auto',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1300,
          width: 'fit-content',
          height: 'fit-content',
          padding: '10px',
        }}
      >
        <ButtonGroup 
          variant="contained" 
          color="primary"
        >
          <Button 
            onClick={() => handleCommand('restart')}
            disabled={isLoading}
            style={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.mode === 'dark' ? '#000' : '#fff',
            }}
          >
            {t('commandsButtonRestart')}
          </Button>
          <Button
            onClick={() => handleCommand('hardreset')}
            disabled={isLoading}
            style={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.mode === 'dark' ? '#000' : '#fff',
            }}
          >
            {t('commandsButtonHardreset')}
          </Button>
          <Button
            onClick={() => handleCommand('change_pool')}
            disabled={isLoading}
            style={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.mode === 'dark' ? '#000' : '#fff',
            }}
          >
            {t('commandsButtonChangePool')}
          </Button>
          <Button
            onClick={() => handleCommand('log')}
            disabled={isLoading}
            style={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.mode === 'dark' ? '#000' : '#fff',
            }}
          >
            {t('commandsButtonRequestLogs')}
          </Button>
          {isClientTicketMode && (
            <Button
              onClick={() => openTicketDialog('manual')}
              disabled={isLoading}
              style={{
                backgroundColor: '#6a1b9a',
                color: '#fff',
              }}
            >
              {t('commandsButtonCreateTicket')}
            </Button>
          )}
          {isAdminOrManager && (
            <Button
              onClick={() => setShowAdminControl(true)}
              disabled={isLoading}
              style={{
                backgroundColor: '#ff9800',
                color: '#fff',
              }}
            >
              {t('commandsButtonAdminControl')}
            </Button>
          )}
        </ButtonGroup>
      </div>

      <Dialog open={openDialog} onClose={() => { setOpenDialog(false); resetForm(); }} maxWidth="md" fullWidth>
        <DialogTitle>
          {isTicketMode
            ? (commandType === 'manual' ? t('commandsDialogNewTicket') : t('commandsDialogTicketForAction', { label: getCommandLabel(commandType) }))
            : (commandType === 'change_pool' ? t('commandsChangePoolTitle') : t('commandsLogTitle'))}
        </DialogTitle>
        <DialogContent>
          {renderDialogContent()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenDialog(false); resetForm(); }}>{t('common:cancel')}</Button>
          <Button
            onClick={handleDialogAction}
            variant="contained"
            disabled={isLoading}
          >
            {isLoading
              ? (isTicketMode ? t('commandsBusyCreating') : t('commandsBusyExecuting'))
              : (isTicketMode ? t('commandsActionCreateTicket') : t('commandsActionExecute'))}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Commands;
